import React from 'react';
import { useSupabaseInventory } from '@/context/SupabaseInventoryContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { TrendingUp, AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
interface SupabaseInventoryChartProps {
  onInventoryClick?: () => void;
  onLowStockClick?: () => void;
}
export const SupabaseInventoryChart: React.FC<SupabaseInventoryChartProps> = ({
  onInventoryClick,
  onLowStockClick
}) => {
  const {
    products,
    getLowStockProducts
  } = useSupabaseInventory();
  const lowStockProducts = getLowStockProducts();

  // Préparer les données pour le graphique
  const chartData = products.map(product => ({
    name: product.name.length > 10 ? product.name.substring(0, 10) + '...' : product.name,
    fullName: product.name,
    quantity: product.quantity,
    threshold: product.min_threshold,
    isLowStock: product.quantity < product.min_threshold
  }));

  // Trier par quantité pour une meilleure visualisation
  chartData.sort((a, b) => b.quantity - a.quantity);
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-foreground">{data.fullName}</p>
          <p className="text-sm text-foreground">Quantité: {data.quantity}</p>
          <p className="text-sm text-foreground">Seuil: {data.threshold}</p>
          {data.isLowStock && <p className="text-sm text-foreground font-medium">Stock faible!</p>}
        </div>;
    }
    return null;
  };
  if (products.length === 0) {
    return <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-gradient-to-br from-red-500/10 to-red-600/20 transition-transform duration-200 hover:scale-105">
            <TrendingUp className="w-6 h-6 text-red-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium mb-1 text-red-700 dark:text-red-300">
              Stock
            </p>
            <p className="text-xl font-bold text-red-800 dark:text-red-200">
              Graphique d'inventaire
            </p>
          </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Aucun produit à afficher</p>
          </div>
        </CardContent>
      </Card>;
  }
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-gradient-to-br from-blue-500/10 to-blue-600/20 transition-transform duration-200 hover:scale-105">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium mb-1 text-blue-700 dark:text-blue-300">
                Stock
              </p>
              <CardTitle className="text-xl font-bold text-blue-800 dark:text-blue-200">
                État de l'inventaire
              </CardTitle>
            </div>
          </div>
          {lowStockProducts.length > 0 && (
            <Badge variant="destructive" className="flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {lowStockProducts.length} stock{lowStockProducts.length > 1 ? 's' : ''} faible{lowStockProducts.length > 1 ? 's' : ''}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData.slice(0, 15)} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="name" 
                className="text-muted-foreground" 
                fontSize={10}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis className="text-muted-foreground" fontSize={12} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="quantity" radius={[2, 2, 0, 0]}>
                {chartData.slice(0, 15).map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.isLowStock ? 'hsl(var(--destructive))' : 'hsl(var(--primary))'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        <div className="flex justify-between items-center mt-4 text-sm text-muted-foreground">
          <span>
            {products.length} produit{products.length > 1 ? 's' : ''} au total
          </span>
          {chartData.length > 15 && (
            <span>
              Affichage des 15 premiers produits
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};